import * as React from "react";
import { ExternalLink } from "lucide-react";

import {
  findCitationById,
  getCitationColor,
  getCitationLabel,
  type Citation,
} from "../citations";

interface CitationCardProps {
  citationId: string;
  citation: Citation;
  /** Optional citation pool used for `findCitationById` lookup (may
   * be wider than the single `citation`, e.g. when the same uuid is
   * indexed under several Citation envelopes). Defaults to `[citation]`. */
  citations?: Citation[];
  onView: (citationId: string) => void;
}

/**
 * 298x95-ish citation card matching Figma node 2645:18621 child
 * "Citation/card". The top row holds a small, color-underlined cited
 * value; the middle row holds the surrounding excerpt; the bottom-right
 * holds a "View Source" badge button that opens the citation drawer.
 *
 * Designed to be ES5/TS-target safe and to gracefully degrade when the
 * pool only carries thin metadata (e.g. missing markdown body).
 */
export const CitationCard: React.FC<CitationCardProps> = ({
  citationId,
  citation,
  citations,
  onView,
}) => {
  const pool = citations ?? [citation];
  const lookup = React.useMemo(() => findCitationById(pool, citationId), [pool, citationId]);

  const dataSource = lookup.metaData?.dataSource ?? citation.metadata?.dataSource;
  const client = citation.client;
  const label = getCitationLabel(dataSource, client);
  const color = getCitationColor(dataSource ?? null);

  const headline = pickHeadline(lookup.content, lookup.metaData?.documentName, citationId);
  const body = pickBody(lookup.content, lookup.metaData?.documentName);

  return (
    <div
      role="group"
      className="flex w-full min-w-0 flex-col gap-2 rounded-lg border border-border bg-background p-3 shadow-sm"
    >
      <div className="flex min-w-0 flex-col items-start">
        <span
          className="inline-flex max-w-full items-center truncate text-[10px] font-medium leading-[12px] text-muted-foreground"
          title={headline}
          style={{ borderBottom: `1px solid ${color}` }}
        >
          {headline}
        </span>
      </div>

      <p className="line-clamp-2 min-w-0 text-[12px] font-medium leading-[18px] text-foreground/90">
        {body}
      </p>

      <div className="flex items-center justify-between gap-2">
        <span
          className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
          style={{ backgroundColor: `${color}33`, color: `${color}` }}
        >
          {label}
        </span>
        <button
          type="button"
          onClick={() => onView(citationId)}
          className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-muted px-2 py-1 text-[10px] font-semibold text-foreground hover:bg-muted/80"
        >
          View Source
          <ExternalLink className="h-3 w-3" aria-hidden />
        </button>
      </div>
    </div>
  );
};

function pickHeadline(
  content: string | undefined,
  documentName: string | undefined,
  citationId: string
): string {
  const first = firstNonEmptyLine(content);
  if (first) return first;
  if (documentName) return documentName;
  return `Citation ${citationId.slice(0, 8)}`;
}

function pickBody(content: string | undefined, documentName: string | undefined): string {
  if (content) {
    const lines = content.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length > 1) return lines.slice(1, 4).join(" ");
    return lines[0] ?? "";
  }
  return documentName ?? "No excerpt available for this citation.";
}

function firstNonEmptyLine(content: string | undefined): string | undefined {
  if (!content) return undefined;
  const lines = content.split(/\r?\n/);
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed) return trimmed.slice(0, 120);
  }
  return undefined;
}
